import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '../services/auth.service';
import type { AccessibleApplication, AccessibleChurch, AuthUser } from '../types/auth.types';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  currentChurchId: string | null;
  currentChurch: AccessibleChurch | null;
  accessibleChurches: AccessibleChurch[];
  applications: AccessibleApplication[];
  setCurrentChurchId: (churchId: string) => void;
  refreshSession: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const readJson = <T,>(key: string): T | null => {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const setStorage = (key: string, value: string | null) => {
  if (typeof window === 'undefined') return;

  if (value === null) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, value);
};

const getChurchIdFromUrl = () => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  return params.get('church_id') ?? params.get('current_church_id');
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(() => readJson<AuthUser>('auth_user'));
  const [currentChurchId, setCurrentChurchIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const urlChurchId = getChurchIdFromUrl();
    if (urlChurchId) {
      window.localStorage.setItem('current_church_id', urlChurchId);
      return urlChurchId;
    }

    return window.localStorage.getItem('current_church_id');
  });
  const [accessibleChurches, setAccessibleChurches] = useState<AccessibleChurch[]>(
    () => readJson<AccessibleChurch[]>('accessible_churches') ?? []
  );
  const [applications, setApplications] = useState<AccessibleApplication[]>([]);
  const currentChurchIdRef = useRef(currentChurchId);

  useEffect(() => {
    currentChurchIdRef.current = currentChurchId;
  }, [currentChurchId]);

  const persistUser = useCallback((nextUser: AuthUser | null) => {
    setUser(nextUser);
    setStorage('auth_user', nextUser ? JSON.stringify(nextUser) : null);
  }, []);

  const setCurrentChurchId = useCallback((churchId: string) => {
    setCurrentChurchIdState(churchId);
    setStorage('current_church_id', churchId);
  }, []);

  const persistChurches = useCallback((churches: AccessibleChurch[]) => {
    setAccessibleChurches(churches);
    setStorage('accessible_churches', JSON.stringify(churches));
  }, []);

  const clearSession = useCallback(() => {
    persistUser(null);
    setCurrentChurchIdState(null);
    setAccessibleChurches([]);
    setApplications([]);
    setStorage('current_church_id', null);
    setStorage('accessible_churches', null);
    setStatus('unauthenticated');
  }, [persistUser]);

  const refreshSession = useCallback(async () => {
    setStatus('loading');

    try {
      const sessionUser = await authService.me();
      persistUser(sessionUser);

      const { currentChurchId: apiCurrentChurchId, churches } = await authService.churches();
      persistChurches(churches);

      const preferredChurchId = getChurchIdFromUrl() ?? currentChurchIdRef.current ?? apiCurrentChurchId ?? churches[0]?.id ?? null;
      if (preferredChurchId) {
        setCurrentChurchId(preferredChurchId);
      }

      authService.applications()
        .then(setApplications)
        .catch(() => setApplications([]));

      setStatus('authenticated');
    } catch {
      clearSession();
    }
  }, [clearSession, persistChurches, persistUser, setCurrentChurchId]);

  const logout = useCallback(async () => {
    await authService.logout().catch(() => undefined);
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const handleUnauthorized = () => clearSession();
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [clearSession]);

  const currentChurch = useMemo(
    () => accessibleChurches.find((church) => church.id === currentChurchId) ?? null,
    [accessibleChurches, currentChurchId]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      currentChurchId,
      currentChurch,
      accessibleChurches,
      applications,
      setCurrentChurchId,
      refreshSession,
      logout,
    }),
    [
      status,
      user,
      currentChurchId,
      currentChurch,
      accessibleChurches,
      applications,
      setCurrentChurchId,
      refreshSession,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};
